'use client'

import { useState, useEffect, useCallback, useRef } from 'react'
import Image from 'next/image'
import { cn } from '@/lib/utils'
import { HelpCircle, Check, X, ChevronRight, RotateCcw, Timer, Zap, Trophy } from 'lucide-react'
import { Button } from '@/components/ui/button'

interface TriviaQuestion {
  id: string
  question: string
  imageUrl?: string
  options: {
    id: string
    text: string
    isCorrect: boolean
  }[]
}

interface TriviaBlockContent {
  title?: string
  isTimed?: boolean
  timePerQuestion?: number
  questions: TriviaQuestion[]
}

interface TriviaBlockProps {
  content: TriviaBlockContent
  className?: string
}

// Scoring constants
const MAX_POINTS = 1000
const MIN_POINTS = 100 // Points if time runs out but answer is correct
const WRONG_ANSWER_POINTS = 0

// Calculate points based on remaining time percentage
function calculatePoints(timeRemaining: number, totalTime: number, isCorrect: boolean): number {
  if (!isCorrect) return WRONG_ANSWER_POINTS
  
  // If time ran out, give minimum points for correct answer
  if (timeRemaining <= 0) return MIN_POINTS
  
  const timePercentage = timeRemaining / totalTime
  // Scale points: 100% time = MAX_POINTS, 0% time = MIN_POINTS
  const points = Math.round(MIN_POINTS + (MAX_POINTS - MIN_POINTS) * timePercentage)
  return points
}

export function TriviaBlock({ content, className }: TriviaBlockProps) {
  const [currentIndex, setCurrentIndex] = useState(0)
  const [selectedAnswer, setSelectedAnswer] = useState<string | null>(null)
  const [hasAnswered, setHasAnswered] = useState(false)
  const [score, setScore] = useState(0)
  const [correctCount, setCorrectCount] = useState(0)
  const [isComplete, setIsComplete] = useState(false)
  const [hasStarted, setHasStarted] = useState(false)
  
  // Timer state
  const [timeRemaining, setTimeRemaining] = useState(content.timePerQuestion || 20)
  const [lastAnswerPoints, setLastAnswerPoints] = useState<number | null>(null)
  const timerRef = useRef<NodeJS.Timeout | null>(null)

  const questions = content.questions || []
  const currentQuestion = questions[currentIndex]
  const isTimed = content.isTimed && (content.timePerQuestion ?? 0) > 0
  const totalTime = content.timePerQuestion || 20

  // Clear timer on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current)
    }
  }, [])

  // Reset timer when moving to next question
  useEffect(() => {
    if (isTimed && hasStarted && !hasAnswered) {
      setTimeRemaining(totalTime)
      
      // Clear existing timer
      if (timerRef.current) clearInterval(timerRef.current)
      
      // Start new timer
      timerRef.current = setInterval(() => {
        setTimeRemaining((prev) => {
          if (prev <= 1) {
            // Time's up - auto-submit with timeout
            handleTimeUp()
            return 0
          }
          return prev - 1
        })
      }, 1000)
    }
    
    return () => {
      if (timerRef.current) clearInterval(timerRef.current)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentIndex, hasStarted, isTimed, totalTime])

  // Stop timer when answered
  useEffect(() => {
    if (hasAnswered && timerRef.current) {
      clearInterval(timerRef.current)
    }
  }, [hasAnswered])

  const handleTimeUp = useCallback(() => {
    if (hasAnswered) return
    
    // Clear timer
    if (timerRef.current) clearInterval(timerRef.current)
    
    // Mark as answered with no selection (time ran out)
    setHasAnswered(true)
    setLastAnswerPoints(0) // No points for timeout without answer
  }, [hasAnswered])

  if (!questions.length) return null

  const handleSelectAnswer = (optionId: string) => {
    if (hasAnswered) return
    
    // Stop the timer
    if (timerRef.current) clearInterval(timerRef.current)
    
    setSelectedAnswer(optionId)
    setHasAnswered(true)
    
    const isCorrect = currentQuestion.options.find(o => o.id === optionId)?.isCorrect ?? false
    
    if (isTimed) {
      // Calculate points based on time remaining
      const points = calculatePoints(timeRemaining, totalTime, isCorrect)
      setScore(score + points)
      setLastAnswerPoints(points)
      if (isCorrect) setCorrectCount(correctCount + 1)
    } else {
      // Simple scoring - 1 point per correct answer
      if (isCorrect) {
        setScore(score + 1)
        setCorrectCount(correctCount + 1)
      }
    }
  }

  const handleNext = () => {
    if (currentIndex < questions.length - 1) {
      setCurrentIndex(currentIndex + 1)
      setSelectedAnswer(null)
      setHasAnswered(false)
      setLastAnswerPoints(null)
    } else {
      setIsComplete(true)
    }
  }

  const handleStart = () => {
    setHasStarted(true)
  }

  const handleRestart = () => {
    setCurrentIndex(0)
    setSelectedAnswer(null)
    setHasAnswered(false)
    setScore(0)
    setCorrectCount(0)
    setIsComplete(false)
    setLastAnswerPoints(null)
    setTimeRemaining(totalTime)
    setHasStarted(false)
    if (timerRef.current) clearInterval(timerRef.current)
  }

  // Start screen for timed quizzes
  if (isTimed && !hasStarted && !isComplete) {
    return (
      <div className={cn('space-y-4', className)}>
        {content.title && (
          <h3 className="text-lg font-semibold text-white flex items-center gap-2">
            <HelpCircle className="h-5 w-5 text-violet-400" />
            {content.title}
          </h3>
        )}
        <div className="p-6 bg-zinc-800/50 rounded-xl border border-zinc-700/50 text-center">
          <div className="inline-flex items-center justify-center p-4 rounded-full bg-gradient-to-br from-amber-500/20 to-orange-500/20 mb-4">
            <Timer className="h-8 w-8 text-amber-400" />
          </div>
          <h4 className="text-xl font-bold text-white mb-2">Timed Quiz</h4>
          <p className="text-zinc-400 mb-4">
            You have <span className="text-amber-400 font-semibold">{totalTime} seconds</span> per question.
            <br />
            Answer quickly for more points!
          </p>
          <div className="flex items-center justify-center gap-6 text-sm text-zinc-500 mb-6">
            <div className="flex items-center gap-1.5">
              <Zap className="h-4 w-4 text-green-400" />
              <span>Fast = {MAX_POINTS} pts</span>
            </div>
            <div className="flex items-center gap-1.5">
              <Timer className="h-4 w-4 text-amber-400" />
              <span>Slow = {MIN_POINTS} pts</span>
            </div>
          </div>
          <Button
            onClick={handleStart}
            className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 text-white font-semibold px-8"
          >
            Start Quiz ({questions.length} questions)
          </Button>
        </div>
      </div>
    )
  }

  // Results screen
  if (isComplete) {
    const percentage = Math.round((correctCount / questions.length) * 100)
    const maxPossibleScore = isTimed ? questions.length * MAX_POINTS : questions.length
    const scorePercentage = isTimed ? Math.round((score / maxPossibleScore) * 100) : percentage
    
    return (
      <div className={cn('space-y-4', className)}>
        {content.title && (
          <h3 className="text-lg font-semibold text-white flex items-center gap-2">
            <HelpCircle className="h-5 w-5 text-violet-400" />
            {content.title}
          </h3>
        )}
        <div className="p-6 bg-zinc-800/50 rounded-xl border border-zinc-700/50 text-center">
          <div className="text-5xl font-bold mb-2">
            {percentage >= 80 ? '🎉' : percentage >= 50 ? '👏' : '💪'}
          </div>
          <h4 className="text-xl font-bold text-white mb-1">
            {percentage >= 80 ? 'Amazing!' : percentage >= 50 ? 'Good Job!' : 'Nice Try!'}
          </h4>
          <p className="text-zinc-400 mb-2">
            You got {correctCount} out of {questions.length} correct ({percentage}%)
          </p>
          {isTimed && (
            <div className="flex items-center justify-center gap-2 mb-4">
              <Trophy className="h-5 w-5 text-amber-400" />
              <span className="text-xl font-bold text-amber-400">{score.toLocaleString()}</span>
              <span className="text-zinc-500">points ({scorePercentage}%)</span>
            </div>
          )}
          <Button
            onClick={handleRestart}
            className="bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:from-violet-500 hover:to-fuchsia-500"
          >
            <RotateCcw className="h-4 w-4 mr-2" />
            Play Again
          </Button>
        </div>
      </div>
    )
  }

  // Timer progress percentage
  const timerPercentage = isTimed ? (timeRemaining / totalTime) * 100 : 100
  const timerColor = timerPercentage > 50 ? 'from-green-500 to-emerald-500' : 
                     timerPercentage > 25 ? 'from-amber-500 to-orange-500' : 
                     'from-red-500 to-rose-500'

  return (
    <div className={cn('space-y-4', className)}>
      {content.title && (
        <h3 className="text-lg font-semibold text-white flex items-center gap-2">
          <HelpCircle className="h-5 w-5 text-violet-400" />
          {content.title}
        </h3>
      )}
      
      <div className="p-5 bg-zinc-800/50 rounded-xl border border-zinc-700/50">
        {/* Progress and Timer */}
        <div className="flex items-center justify-between mb-4">
          <span className="text-sm text-zinc-400">
            Question {currentIndex + 1} of {questions.length}
          </span>
          <div className="flex items-center gap-3">
            {isTimed && (
              <div className="flex items-center gap-2">
                <Timer className={cn(
                  "h-4 w-4",
                  timerPercentage > 50 ? "text-green-400" :
                  timerPercentage > 25 ? "text-amber-400" : "text-red-400 animate-pulse"
                )} />
                <span className={cn(
                  "font-mono font-bold text-lg min-w-[2.5rem] text-right",
                  timerPercentage > 50 ? "text-green-400" :
                  timerPercentage > 25 ? "text-amber-400" : "text-red-400"
                )}>
                  {timeRemaining}s
                </span>
              </div>
            )}
            <span className={cn(
              "text-sm font-medium",
              isTimed ? "text-amber-400" : "text-violet-400"
            )}>
              {isTimed ? `${score.toLocaleString()} pts` : `Score: ${score}`}
            </span>
          </div>
        </div>

        {/* Timer bar (timed mode only) */}
        {isTimed && (
          <div className="h-2 bg-zinc-700 rounded-full mb-5 overflow-hidden">
            <div 
              className={cn(
                "h-full rounded-full transition-all duration-1000 ease-linear bg-gradient-to-r",
                timerColor
              )}
              style={{ width: `${timerPercentage}%` }}
            />
          </div>
        )}

        {/* Progress bar */}
        <div className="h-1 bg-zinc-700 rounded-full mb-5 overflow-hidden">
          <div 
            className="h-full bg-gradient-to-r from-violet-500 to-fuchsia-500 transition-all duration-300"
            style={{ width: `${((currentIndex + (hasAnswered ? 1 : 0)) / questions.length) * 100}%` }}
          />
        </div>

        {/* Question image */}
        {currentQuestion.imageUrl && (
          <div className="mb-4 rounded-lg overflow-hidden max-w-sm mx-auto relative aspect-video">
            <Image
              src={currentQuestion.imageUrl}
              alt="Question"
              fill
              sizes="(max-width: 768px) 100vw, 384px"
              className="object-contain"
            />
          </div>
        )}

        {/* Question */}
        <p className="text-lg text-white font-medium mb-5">
          {currentQuestion.question}
        </p>

        {/* Options */}
        <div className="space-y-2 mb-5">
          {currentQuestion.options.map((option, idx) => {
            const isSelected = selectedAnswer === option.id
            const showCorrect = hasAnswered && option.isCorrect
            const showWrong = hasAnswered && isSelected && !option.isCorrect

            return (
              <button
                key={option.id}
                onClick={() => handleSelectAnswer(option.id)}
                disabled={hasAnswered}
                className={cn(
                  'w-full flex items-center gap-3 p-4 rounded-lg border text-left transition-all',
                  !hasAnswered && 'hover:border-violet-500/50 hover:bg-violet-500/10',
                  !hasAnswered && isSelected && 'border-violet-500 bg-violet-500/20',
                  !hasAnswered && !isSelected && 'border-zinc-700 bg-zinc-800/50',
                  showCorrect && 'border-green-500 bg-green-500/20',
                  showWrong && 'border-red-500 bg-red-500/20',
                  hasAnswered && !showCorrect && !showWrong && 'border-zinc-700 bg-zinc-800/30 opacity-50'
                )}
              >
                <span className={cn(
                  'w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium',
                  !hasAnswered && 'bg-zinc-700 text-zinc-300',
                  showCorrect && 'bg-green-500 text-white',
                  showWrong && 'bg-red-500 text-white',
                  hasAnswered && !showCorrect && !showWrong && 'bg-zinc-700 text-zinc-500'
                )}>
                  {showCorrect ? (
                    <Check className="h-4 w-4" />
                  ) : showWrong ? (
                    <X className="h-4 w-4" />
                  ) : (
                    String.fromCharCode(65 + idx)
                  )}
                </span>
                <span className={cn(
                  'flex-1',
                  showCorrect && 'text-green-400 font-medium',
                  showWrong && 'text-red-400',
                  !hasAnswered && 'text-white',
                  hasAnswered && !showCorrect && !showWrong && 'text-zinc-500'
                )}>
                  {option.text}
                </span>
              </button>
            )
          })}
        </div>

        {/* Points feedback for timed mode */}
        {hasAnswered && isTimed && lastAnswerPoints !== null && (
          <div className={cn(
            "text-center mb-4 py-2 px-4 rounded-lg",
            lastAnswerPoints > 0 ? "bg-green-500/20" : "bg-red-500/20"
          )}>
            {lastAnswerPoints > 0 ? (
              <div className="flex items-center justify-center gap-2">
                <Zap className="h-5 w-5 text-green-400" />
                <span className="font-bold text-green-400">+{lastAnswerPoints} points!</span>
                {lastAnswerPoints >= MAX_POINTS * 0.8 && (
                  <span className="text-xs text-green-300 ml-1">⚡ Speed bonus!</span>
                )}
              </div>
            ) : selectedAnswer ? (
              <span className="text-red-400">Incorrect answer</span>
            ) : (
              <span className="text-amber-400">Time&apos;s up!</span>
            )}
          </div>
        )}

        {/* Next button */}
        {hasAnswered && (
          <Button
            onClick={handleNext}
            className="w-full bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:from-violet-500 hover:to-fuchsia-500"
          >
            {currentIndex < questions.length - 1 ? (
              <>
                Next Question
                <ChevronRight className="h-4 w-4 ml-2" />
              </>
            ) : (
              'See Results'
            )}
          </Button>
        )}
      </div>
    </div>
  )
}
